import React, { useMemo, useState } from "react";
import moment from "moment";
import "./interviewReadiness.scss";

// Shared by the voice-call entry pages (ScheduleStage and DynamicVoiceInterviewMain).
// Each page passes its own brand accent through the --ir-accent custom property.

export const BeforeYouBeginModal = ({ showNotReady, onStart, onNotReady, accentColor }) => (
  <div
    className="ir-overlay"
    role="dialog"
    aria-modal="true"
    style={accentColor ? { "--ir-accent": accentColor } : undefined}
  >
    <div className="ir-card">
      <h3>Before You Begin Your Interview</h3>
      <p>
        This interview takes <strong>10 to 20 minutes</strong> and can't be paused once started.
        Please begin only when you're in a quiet place with a stable internet connection.
      </p>
      <div className="ir-actions">
        {showNotReady && (
          <button type="button" className="ir-btn-secondary" onClick={onNotReady}>
            Not ready right now?
          </button>
        )}
        <button type="button" className="ir-btn-primary" onClick={onStart}>
          Start now
        </button>
      </div>
    </div>
  </div>
);

// ─── Resend response → UI outcome ─────────────────────────────────────────────
// Outcome types:
//   sent     – email went out; button stays only if remaining_resends > 0
//   noResend – resending can't work (no email / already resent); hide the button
//   blocked  – the link itself is unusable; show only the message and Close
//   retry    – transient or unexpected failure; keep the button enabled

const INVALID_LINK = "This link isn't valid. Please use the link from your email.";
const SEND_FAILED = "We couldn't send the email right now. Please try again in a moment.";
const GENERIC_ERROR = "Something went wrong. Please refresh the page and try again.";

const ALREADY_RESENT =
  "We've already resent the link to your email. Please check your inbox and spam folder. Still not received? Contact the recruiter.";

const NO_RESEND_DETAILS = {
  "No email address on file for this candidate":
    "We don't have an email address for you. Please use the link from your chat or contact the recruiter.",
  "The invite email has already been resent. Please check your inbox and spam folder.": ALREADY_RESENT,
};

const BLOCKED_DETAILS = {
  "This session is not a dynamic voice call": INVALID_LINK,
  "This stage is not a voice screening stage": INVALID_LINK,
  "This stage uses a dynamic voice call link": "Please use the voice screening link from your email.",
  "Invalid verification code": INVALID_LINK,
  "The Link has been expired": "This interview link has expired. Please contact the recruiter.",
  "This screening call has already been completed": "You've already completed this interview. Thank you!",
  "This screening call has already ended": "This interview session has ended. Check your email for a new link.",
  "This screening was closed due to inactivity":
    "This interview was closed due to inactivity. Please contact the recruiter.",
  "This screening is no longer available":
    "This interview is no longer available. Please contact the recruiter.",
  "Voice call session not found": INVALID_LINK,
  "Hiring stage not found": INVALID_LINK,
  "Your screening is already in progress": "Your interview is already in progress.",
  "Your call is already scheduled": "Your call is already scheduled. We'll call you at the time you chose.",
};

export const getResendOutcome = (res) => {
  if (res?.success) {
    return {
      type: "sent",
      maskedEmail: res.data?.masked_email || "",
      // Read the limit from the API rather than hard-coding it; missing → treat as used up.
      canResendAgain: Number(res.data?.remaining_resends) > 0,
    };
  }

  const status = res?.status || 0;
  const detail = (res?.detail || "").trim();

  if (NO_RESEND_DETAILS[detail]) return { type: "noResend", message: NO_RESEND_DETAILS[detail] };
  if (BLOCKED_DETAILS[detail]) return { type: "blocked", message: BLOCKED_DETAILS[detail] };

  // 429 = the candidate's resend was already used (e.g. reloaded or another device).
  if (status === 429) return { type: "noResend", message: ALREADY_RESENT };
  // Unrecognised 403/404/409 still mean the link can't be used from here.
  if ([403, 404, 409].includes(status)) return { type: "blocked", message: detail || INVALID_LINK };
  if (status >= 500) return { type: "retry", message: SEND_FAILED };
  return { type: "retry", message: GENERIC_ERROR };
};

// onResend resolves to the resendInterviewInvite result.
export const NotReadyNotice = ({ onResend, accentColor }) => {
  const [sending, setSending] = useState(false);
  const [outcome, setOutcome] = useState(null);
  const [closeFailed, setCloseFailed] = useState(false);
  const deadline = useMemo(() => moment().add(1, "day").format("YYYY-MM-DD"), []);

  const type = outcome?.type;
  const showResendButton =
    type !== "noResend" && type !== "blocked" && !(type === "sent" && !outcome.canResendAgain);

  const handleResend = async () => {
    if (sending || !onResend) return;
    setSending(true);
    try {
      setOutcome(getResendOutcome(await onResend()));
    } catch (err) {
      console.error("Failed to resend interview email", err);
      setOutcome({ type: "retry", message: SEND_FAILED });
    } finally {
      setSending(false);
    }
  };

  // Browsers only let a script close a tab it opened itself, so fall back to
  // telling the candidate to close it manually if the tab is still open.
  const handleClose = () => {
    window.close();
    setTimeout(() => setCloseFailed(true), 300);
  };

  let statusMessage = null;
  if (type === "sent") {
    statusMessage = (
      <p className="ir-resent-msg">
        We have sent you mail once again
        {outcome.maskedEmail ? ` at ${outcome.maskedEmail}` : ""}. It may take a few minutes to
        arrive, so check your spam folder too.
      </p>
    );
  } else if (outcome?.message) {
    statusMessage = <p className="ir-resend-error">{outcome.message}</p>;
  }

  return (
    <div
      className="ir-not-ready"
      style={accentColor ? { "--ir-accent": accentColor } : undefined}
    >
      {type !== "blocked" && (
        <>
          <h4>Not ready right now?</h4>
          <p>
            Complete your interview anytime before <strong>{deadline}</strong>. We've emailed you
            the link, so check your inbox. If you haven’t received the email, click Resend Email to
            receive the link again.
          </p>
        </>
      )}
      {statusMessage}
      {closeFailed ? (
        <p className="ir-close-fallback">You can now safely close this browser tab.</p>
      ) : (
        <div className="ir-actions">
          {showResendButton && (
            <button
              type="button"
              className="ir-btn-secondary"
              onClick={handleResend}
              disabled={sending}
            >
              {sending ? "Sending…" : "Resend Email"}
            </button>
          )}
          <button type="button" className="ir-btn-primary" onClick={handleClose}>
            Close
          </button>
        </div>
      )}
    </div>
  );
};
